import React, { useState, useRef } from 'react';
import api from '../services/api';
import {
  BugAntIcon, LinkIcon, ArrowUpTrayIcon, MagnifyingGlassIcon,
  CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon,
  GlobeAltIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';

export default function MalwarePage() {
  const [tab, setTab] = useState('url');
  const [url, setUrl] = useState('');
  const [scanSubdomains, setScanSubdomains] = useState(true);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  async function handleScanURL(e) {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    setProgress('Mengirim URL untuk analisis...');

    try {
      setProgress('Menunggu hasil analisis (bisa 30-60 detik)...');
      const res = await api.post('/malware/scan-url', {
        url: url.trim(),
        scan_subdomains: scanSubdomains,
      }, { timeout: 180000 });

      setResult(res.data);
      setProgress('');
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal scan URL');
      setProgress('');
    } finally {
      setLoading(false);
    }
  }

  async function handleScanFile(e) {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    setError('');
    setResult(null);
    setProgress('Mengupload file untuk analisis...');

    try {
      const form = new FormData();
      form.append('file', file);
      setProgress('Menunggu hasil analisis file (bisa 1-2 menit)...');
      const res = await api.post('/malware/scan-file', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setResult(res.data);
      setProgress('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal scan file');
      setProgress('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <BugAntIcon className="w-6 h-6 text-cyber-accent" /> Malware Scanner
        </h1>
      </div>

      {/* Form */}
      <div className="cyber-card p-5">
        <div className="flex gap-2 mb-5">
          <button onClick={() => { setTab('url'); setResult(null); setError(''); }}
            className={`cyber-btn flex items-center gap-2 ${tab === 'url' ? 'bg-cyber-accent text-cyber-bg' : 'border border-cyber-border text-gray-400 hover:text-gray-200'}`}>
            <LinkIcon className="w-4 h-4" /> Scan URL
          </button>
          <button onClick={() => { setTab('file'); setResult(null); setError(''); }}
            className={`cyber-btn flex items-center gap-2 ${tab === 'file' ? 'bg-cyber-accent text-cyber-bg' : 'border border-cyber-border text-gray-400 hover:text-gray-200'}`}>
            <ArrowUpTrayIcon className="w-4 h-4" /> Upload File
          </button>
        </div>

        {tab === 'url' ? (
          <div className="space-y-3">
            <div className="flex gap-3">
              <input type="url" className="cyber-input flex-1" placeholder="https://example.com"
                value={url} onChange={(e) => setUrl(e.target.value)} disabled={loading} />
              <button onClick={handleScanURL} disabled={loading || !url}
                className="cyber-btn-primary flex items-center gap-2 whitespace-nowrap disabled:opacity-50">
                <MagnifyingGlassIcon className="w-4 h-4" />
                {loading ? 'Scanning...' : 'Scan URL'}
              </button>
            </div>
            <label className="flex items-center gap-2 cursor-pointer w-fit" onClick={() => !loading && setScanSubdomains(!scanSubdomains)}>
              <div className={`relative w-10 h-5 rounded-full transition-colors ${scanSubdomains ? 'bg-cyber-accent' : 'bg-cyber-border'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${scanSubdomains ? 'left-5' : 'left-0.5'}`} />
              </div>
              <span className="text-sm text-gray-400 flex items-center gap-1">
                <GlobeAltIcon className="w-4 h-4" /> Scan semua subdomain
              </span>
            </label>
          </div>
        ) : (
          <form onSubmit={handleScanFile} className="space-y-3">
            <div className="border-2 border-dashed border-cyber-border rounded-lg p-8 text-center cursor-pointer hover:border-cyber-accent transition-colors"
              onClick={() => !loading && fileRef.current?.click()}>
              <ArrowUpTrayIcon className="w-8 h-8 mx-auto mb-2 text-gray-500" />
              <p className="text-gray-400 text-sm">{file ? file.name : 'Klik atau drag file ke sini'}</p>
              <p className="text-gray-600 text-xs mt-1">Maks. 32MB</p>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
            </div>
            <button type="submit" disabled={loading || !file} className="cyber-btn-primary flex items-center gap-2 disabled:opacity-50">
              <ArrowUpTrayIcon className="w-4 h-4" />
              {loading ? 'Scanning...' : 'Upload & Scan'}
            </button>
          </form>
        )}

        {/* Progress */}
        {loading && progress && (
          <div className="mt-4 flex items-center gap-3 bg-cyber-accent bg-opacity-5 border border-cyber-accent border-opacity-20 rounded-lg px-4 py-3">
            <div className="w-4 h-4 border-2 border-cyber-accent border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <span className="text-sm text-cyber-accent">{progress}</span>
          </div>
        )}

        {error && (
          <div className="mt-3 text-sm px-3 py-2 rounded-lg bg-red-900 bg-opacity-30 text-red-400 border border-red-800 border-opacity-40">
            ❌ {error}
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Main result */}
          <ScanResultCard data={result.main} label="Scan Utama" />

          {/* Subdomain results */}
          {result.subdomains?.length > 0 && (
            <>
              <div className="flex items-center gap-2 pt-2">
                <GlobeAltIcon className="w-5 h-5 text-cyber-accent" />
                <h2 className="text-sm font-bold text-gray-300">
                  Hasil Scan Subdomain ({result.subdomains.length} subdomain)
                </h2>
              </div>
              {result.subdomains.map((sub, i) => (
                <ScanResultCard key={i} data={sub} label={`Subdomain: ${sub.subdomain}`} />
              ))}
            </>
          )}

          {result.subdomains?.length === 0 && scanSubdomains && (
            <div className="cyber-card p-4 text-sm text-gray-500 flex items-center gap-2">
              <GlobeAltIcon className="w-4 h-4" />
              Tidak ditemukan subdomain untuk domain ini.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Scan Result Card ────────────────────────────────────────────────────────
function ScanResultCard({ data, label }) {
  const [showClean, setShowClean] = useState(false);

  if (!data) return null;

  const engines = data.engines || [];
  const stats = data.stats || {};

  const malicious = engines.filter(e => e.category === 'malicious');
  const suspicious = engines.filter(e => e.category === 'suspicious');
  const harmless = engines.filter(e => e.category === 'harmless');
  const undetected = engines.filter(e => e.category === 'undetected');
  const clean = [...harmless, ...undetected];
  const threats = [...malicious, ...suspicious];
  const isClean = threats.length === 0;
  const isError = data.status === 'error';

  const borderColor = isError ? 'border-gray-600' : isClean ? 'border-green-700' : data.status === 'infected' ? 'border-red-700' : 'border-yellow-700';
  const bgColor = isError ? '' : isClean ? 'bg-green-900 bg-opacity-5' : data.status === 'infected' ? 'bg-red-900 bg-opacity-5' : 'bg-yellow-900 bg-opacity-5';

  return (
    <div className={`cyber-card p-5 border ${borderColor} ${bgColor}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3 gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-500 mb-1">{label}</div>
          <div className="font-mono text-xs text-gray-300 break-all bg-cyber-bg rounded px-2 py-1">
            {data.url || data.file_name || '—'}
          </div>
        </div>
        <StatusBadgeLarge status={data.status} />
      </div>

      {isError && (
        <div className="text-sm text-red-400 mb-3">⚠ Error: {data.error}</div>
      )}

      {/* Stats */}
      {!isError && engines.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-5">
          <StatBox label="Malicious" value={stats.malicious ?? malicious.length} color={malicious.length > 0 ? 'text-cyber-red' : 'text-gray-600'} bg={malicious.length > 0 ? 'bg-red-900 bg-opacity-30 border border-red-800 border-opacity-40' : 'bg-cyber-bg'} />
          <StatBox label="Suspicious" value={stats.suspicious ?? suspicious.length} color={suspicious.length > 0 ? 'text-cyber-yellow' : 'text-gray-600'} bg={suspicious.length > 0 ? 'bg-yellow-900 bg-opacity-30 border border-yellow-800 border-opacity-40' : 'bg-cyber-bg'} />
          <StatBox label="Harmless" value={stats.harmless ?? harmless.length} color="text-cyber-green" bg="bg-cyber-bg" />
          <StatBox label="Undetected" value={stats.undetected ?? undetected.length} color="text-gray-500" bg="bg-cyber-bg" />
        </div>
      )}

      {/* Clean banner */}
      {isClean && engines.length > 0 && (
        <div className="flex items-center gap-3 bg-green-900 bg-opacity-20 border border-green-700 border-opacity-50 rounded-lg px-4 py-3 mb-4">
          <CheckCircleIcon className="w-6 h-6 text-cyber-green flex-shrink-0" />
          <div>
            <div className="text-sm font-bold text-cyber-green">✓ Tidak ada ancaman terdeteksi</div>
            <div className="text-xs text-gray-400 mt-0.5">{clean.length} security engine menyatakan bersih</div>
          </div>
        </div>
      )}

      {/* Malicious engines */}
      {malicious.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <XCircleIcon className="w-4 h-4 text-cyber-red" />
            <span className="text-xs font-bold text-cyber-red uppercase tracking-wider">
              ⚠ TERDETEKSI BERBAHAYA — {malicious.length} Engine
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {malicious.map((e, i) => (
              <div key={i} className="flex items-center justify-between bg-red-900 bg-opacity-25 border border-red-700 border-opacity-50 rounded-lg px-3 py-2">
                <span className="text-xs font-semibold text-red-300 flex-shrink-0">{e.engine}</span>
                <span className="text-xs text-red-400 font-mono ml-3 truncate" title={e.result}>
                  {e.result || 'malicious'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suspicious engines */}
      {suspicious.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-cyber-yellow" />
            <span className="text-xs font-bold text-cyber-yellow uppercase tracking-wider">
              MENCURIGAKAN — {suspicious.length} Engine
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {suspicious.map((e, i) => (
              <div key={i} className="flex items-center justify-between bg-yellow-900 bg-opacity-25 border border-yellow-700 border-opacity-50 rounded-lg px-3 py-2">
                <span className="text-xs font-semibold text-yellow-300 flex-shrink-0">{e.engine}</span>
                <span className="text-xs text-yellow-400 font-mono ml-3 truncate" title={e.result}>
                  {e.result || 'suspicious'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toggle clean engines */}
      {clean.length > 0 && (
        <div>
          <button onClick={() => setShowClean(!showClean)}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors mb-2">
            <ShieldCheckIcon className="w-4 h-4 text-cyber-green" />
            {showClean ? '▲ Sembunyikan' : `▼ Tampilkan ${clean.length} engine yang menyatakan bersih`}
          </button>

          {showClean && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 max-h-64 overflow-y-auto pr-1">
              {clean.map((e, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-cyber-bg rounded px-2 py-1.5">
                  <CheckCircleIcon className="w-3 h-3 text-cyber-green flex-shrink-0" />
                  <span className="text-xs text-gray-400 truncate">{e.engine}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color, bg }) {
  return (
    <div className={`rounded-lg p-3 text-center ${bg}`}>
      <div className={`text-2xl font-bold ${color}`}>{value ?? 0}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function StatusBadgeLarge({ status }) {
  const map = {
    clean: { label: '✓ BERSIH', cls: 'bg-green-900 text-green-300 border border-green-700' },
    suspicious: { label: '⚠ MENCURIGAKAN', cls: 'bg-yellow-900 text-yellow-300 border border-yellow-700' },
    infected: { label: '✕ TERINFEKSI', cls: 'bg-red-900 text-red-300 border border-red-700' },
    error: { label: '— ERROR', cls: 'bg-gray-800 text-gray-400 border border-gray-700' },
    queued: { label: '⟳ ANTRIAN', cls: 'bg-gray-800 text-gray-400' },
  };
  const cfg = map[status] || map.queued;
  return <span className={`px-3 py-1 rounded-lg text-xs font-bold whitespace-nowrap ${cfg.cls}`}>{cfg.label}</span>;
}
